import { AdminMembersReportEventsTableToolbarState } from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTable"
import AdminMembersReportEventsTableAttendedFilter from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableAttendedFilter"
import AdminMembersReportEventsTableConfirmedFilter from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableConfirmedFilter"
import AdminMembersReportEventsTableExportCSVButton from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableExportCSVButton"
import AdminMembersReportEventsTableSortDropdown from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableSortDropdown"
import AdminMembersReportEventsTableStatusFilter from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableStatusFilter"
import AdminMembersReportEventsTableViewedRecordingFilter from "@/admin/insights/member-reports/tabs/events/AdminMembersReportEventsTableViewedRecordingFilter"
import { useLabel } from "@/core/context/LabelsContext"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import AppFilterDropdown from "@components/dropdown/app/AppFilterDropdown"
import ProductFilterDropdown from "@components/dropdown/product/ProductFilterDropdown"
import { DiscoButton, DiscoButtonSkeleton, DiscoDropdown, DiscoText } from "@disco-ui"
import DiscoTableFilterDropdown from "@disco-ui/table/header/search/DiscoTableFilterDropdown"
import { DiscoTableSortDropdownSkeleton } from "@disco-ui/table/header/search/DiscoTableSortDropdown"
import { observer } from "mobx-react-lite"
import React from "react"

interface AdminMembersReportEventsTableToolbarProps {
  toolbarState: AdminMembersReportEventsTableToolbarState
  setToolbarState: React.Dispatch<
    React.SetStateAction<AdminMembersReportEventsTableToolbarState>
  >
}

function AdminMembersReportEventsTableToolbar({
  toolbarState,
  setToolbarState,
}: AdminMembersReportEventsTableToolbarProps) {
  const classes = useStyles()
  const experienceLabel = useLabel("admin_experience")

  return (
    <div className={classes.container}>
      <DiscoDropdown
        testid={"AdminMembersReportEventsTableToolbar.filter-select"}
        menuClasses={{
          paper: classes.filterDropdownMenu,
        }}
        menuButton={(btnProps) => (
          <DiscoButton
            {...btnProps}
            data-testid={
              "AdminMembersReportEventsTableToolbar.filter-select.header-button"
            }
            className={classes.dropdownButton}
          >
            {"Filters"}
          </DiscoButton>
        )}
      >
        <DiscoText
          variant={"body-xs-500"}
          marginTop={0.5}
          marginBottom={0.5}
          color={"text.secondary"}
        >
          {"Filter by..."}
        </DiscoText>

        {/* Event Location filter dropdown */}
        <DiscoTableFilterDropdown
          testid={"AdminMembersReportEventsTableToolbar.location-select"}
          isNested
          dropdown={{
            label: "Event Location",
          }}
        >
          <AppFilterDropdown
            testid={"AdminMembersReportEventsTableToolbar.location-select.app-select"}
            title={"Apps"}
            kind={"organization_events"}
            filterState={toolbarState}
            setFilterState={(newToolbarState) =>
              setToolbarState({
                ...toolbarState,
                ...newToolbarState,
              })
            }
          />

          <ProductFilterDropdown
            testid={"AdminMembersReportEventsTableToolbar.location-select.product-select"}
            title={experienceLabel.plural}
            hideCommunityOption
            filterState={toolbarState}
            setFilterState={(newToolbarState) =>
              setToolbarState({
                ...toolbarState,
                ...newToolbarState,
              })
            }
          />
        </DiscoTableFilterDropdown>

        {/* Status filter dropdown */}
        <AdminMembersReportEventsTableStatusFilter
          filterState={toolbarState}
          setFilterState={(newToolbarState) =>
            setToolbarState({
              ...toolbarState,
              ...newToolbarState,
            })
          }
        />

        {/* Confirmed filter dropdown */}
        <AdminMembersReportEventsTableConfirmedFilter
          filterState={toolbarState}
          setFilterState={(newToolbarState) =>
            setToolbarState({
              ...toolbarState,
              ...newToolbarState,
            })
          }
        />

        {/* Attended filter dropdown */}
        <AdminMembersReportEventsTableAttendedFilter
          filterState={toolbarState}
          setFilterState={(newToolbarState) =>
            setToolbarState({
              ...toolbarState,
              ...newToolbarState,
            })
          }
        />

        {/* ViewedRecording filter dropdown */}
        <AdminMembersReportEventsTableViewedRecordingFilter
          filterState={toolbarState}
          setFilterState={(newToolbarState) =>
            setToolbarState({
              ...toolbarState,
              ...newToolbarState,
            })
          }
        />
      </DiscoDropdown>

      {/* Sort */}
      <AdminMembersReportEventsTableSortDropdown
        sortState={toolbarState}
        setSortState={(newSortState) =>
          setToolbarState({
            ...toolbarState,
            ...newSortState,
          })
        }
      />

      {/* Export CSV */}
      <AdminMembersReportEventsTableExportCSVButton toolbarState={toolbarState} />
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  container: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    width: "100%",
    justifyContent: "flex-end",

    [theme.breakpoints.down("md")]: {
      justifyContent: "flex-start",
    },
  },
  filterDropdownMenu: {
    minWidth: "200px",
  },
  dropdownButton: {
    minWidth: "140px",
    justifyContent: "space-between",
  },
}))

export const AdminMembersReportEventsTableToolbarSkeleton: React.FC = () => {
  const classes = useStyles()
  return (
    <div className={classes.container}>
      <DiscoTableSortDropdownSkeleton />
      <DiscoTableSortDropdownSkeleton />
      <DiscoButtonSkeleton width={"150px"} />
    </div>
  )
}

export default Relay.withSkeleton<AdminMembersReportEventsTableToolbarProps>({
  component: observer(AdminMembersReportEventsTableToolbar),
  skeleton: AdminMembersReportEventsTableToolbarSkeleton,
})
